import { z as supportsANSIColors, f as formatTimestamp } from "./environment-COWvu6Wz.js";
const ANSI_CODES = {
  // Colors
  black: 30,
  red: 31,
  green: 32,
  yellow: 33,
  blue: 34,
  cyan: 36,
  white: 37,
  // Bright colors
  brightBlack: 90,
  bgRed: 41,
  bgGreen: 42,
  bgYellow: 43,
  // Styles
  reset: 0,
  bold: 1,
  dim: 2,
  underline: 4,
  blink: 5
};
const ANSI_LEVEL_STYLES = {
  debug: {
    color: ANSI_CODES.brightBlack,
    styles: [ANSI_CODES.dim],
    emoji: "🔍"
  },
  info: {
    color: ANSI_CODES.blue,
    styles: [ANSI_CODES.bold],
    emoji: "ℹ️"
  },
  warn: {
    color: ANSI_CODES.yellow,
    background: ANSI_CODES.bgYellow,
    styles: [ANSI_CODES.bold, ANSI_CODES.black],
    emoji: "⚠️"
  },
  error: {
    color: ANSI_CODES.red,
    background: ANSI_CODES.bgRed,
    styles: [ANSI_CODES.bold],
    emoji: "❌"
  },
  critical: {
    color: ANSI_CODES.white,
    background: ANSI_CODES.bgRed,
    styles: [ANSI_CODES.bold, ANSI_CODES.blink],
    emoji: "🚨"
  },
  success: {
    color: ANSI_CODES.green,
    background: ANSI_CODES.bgGreen,
    styles: [ANSI_CODES.bold],
    emoji: "✅"
  }
};
function ansi(code) {
  const codes = Array.isArray(code) ? code : [code];
  return `\x1B[${codes.join(";")}m`;
}
function ansiStyle(text, codes) {
  if (!supportsANSIColors()) return text;
  return `${ansi(codes)}${text}${ansi(ANSI_CODES.reset)}`;
}
function ansiColor(text, color) {
  return ansiStyle(text, [color]);
}
function ansiBackground(text, bg, color) {
  if (color) {
    return ansiStyle(text, [color, bg]);
  }
  return ansiStyle(text, [bg]);
}
function ansiBold(text) {
  return ansiStyle(text, [ANSI_CODES.bold]);
}
function ansiDim(text) {
  return ansiStyle(text, [ANSI_CODES.dim]);
}
function ansiUnderline(text) {
  return ansiStyle(text, [ANSI_CODES.underline]);
}
function formatTimestampANSI(timestamp) {
  return ansiColor(timestamp, ANSI_CODES.brightBlack);
}
function formatLogLevelANSI(level, useEmojis = true) {
  const style = ANSI_LEVEL_STYLES[level];
  const emoji = useEmojis ? style.emoji : "";
  const label = level.toUpperCase().padEnd(8);
  if (!supportsANSIColors()) {
    return `${emoji} ${label}`;
  }
  let codes = [style.color, ...style.styles];
  if (style.background) {
    codes.push(style.background);
  }
  return ansiStyle(`${emoji} ${label}`, codes);
}
function formatSuccessANSI(message, useEmojis = true) {
  const style = ANSI_LEVEL_STYLES.success;
  const emoji = useEmojis ? style.emoji : "";
  if (!supportsANSIColors()) {
    return `${emoji} SUCCESS: ${message}`;
  }
  const prefix = ansiStyle(`${emoji} SUCCESS:`, [style.color, ...style.styles]);
  return `${prefix} ${message}`;
}
function formatPrefixANSI(prefix) {
  if (!supportsANSIColors()) {
    return `[${prefix}]`;
  }
  return ansiStyle(`[${prefix}]`, [ANSI_CODES.cyan, ANSI_CODES.bold]);
}
function formatLocationANSI(location) {
  if (!supportsANSIColors()) {
    return `(${location})`;
  }
  return ansiColor(`(${location})`, ANSI_CODES.brightBlack);
}
const BUILD_FORMATTERS = {
  /**
   * Format compilation step
   */
  compilation: (step, status = "starting") => {
    const stepText = ansiBold(step);
    switch (status) {
      case "starting":
        return `${ansiColor("⚙️", ANSI_CODES.blue)} ${stepText}...`;
      case "completed":
        return `${ansiColor("✓", ANSI_CODES.green)} ${stepText} ${ansiColor("completed", ANSI_CODES.green)}`;
      case "failed":
        return `${ansiColor("✗", ANSI_CODES.red)} ${stepText} ${ansiColor("failed", ANSI_CODES.red)}`;
      default:
        return stepText;
    }
  },
  /**
   * Format warning message
   */
  warning: (message) => {
    return `${ansiColor("⚠", ANSI_CODES.yellow)} ${ansiStyle("Warning:", [ANSI_CODES.yellow, ANSI_CODES.bold])} ${message}`;
  },
  /**
   * Format error message
   */
  error: (message) => {
    return `${ansiColor("✗", ANSI_CODES.red)} ${ansiStyle("Error:", [ANSI_CODES.red, ANSI_CODES.bold])} ${message}`;
  },
  /**
   * Format success message
   */
  success: (message) => {
    return `${ansiColor("✓", ANSI_CODES.green)} ${ansiColor(message, ANSI_CODES.green)}`;
  },
  /**
   * Format info message
   */
  info: (message) => {
    return `${ansiColor("ℹ", ANSI_CODES.blue)} ${message}`;
  }
};
const EMOJI_ALTERNATIVES = {
  "🔍": "[DEBUG]",
  "ℹ️": "[INFO]",
  "⚠️": "[WARN]",
  "❌": "[ERROR]",
  "🚨": "[CRITICAL]",
  "✅": "[OK]",
  "✓": "[OK]",
  "✗": "[FAIL]",
  "⚙️": "[BUILD]",
  "📦": "[PKG]"
};
function sanitizeEmojis(text) {
  const isModernTerminal = process.env.TERM && (process.env.TERM.includes("xterm") || process.env.TERM.includes("screen") || process.env.TERM.includes("tmux"));
  const isCI = process.env.CI || process.env.GITHUB_ACTIONS || process.env.JENKINS_URL;
  if (isModernTerminal && !isCI) {
    return text;
  }
  let result = text;
  Object.entries(EMOJI_ALTERNATIVES).forEach(([emoji, alt]) => {
    result = result.replace(new RegExp(emoji, "g"), alt);
  });
  return result;
}
function createOutput(level, message, prefix, stackInfo, format = "auto") {
  const detectedFormat = format === "auto" ? detectOptimalFormat() : format;
  switch (detectedFormat) {
    case "ansi":
      return createANSIOutput(level, message, prefix, stackInfo);
    case "build":
      return createBuildOutput(level, message, prefix, stackInfo);
    case "ci":
      return createCIOutput(level, message, prefix, stackInfo);
    case "plain":
    default:
      return createPlainOutput(level, message, prefix, stackInfo);
  }
}
function createPlainOutput(level, message, prefix, stackInfo) {
  const timestamp = formatTimestamp();
  const timeStr = timestamp.slice(11, 23);
  let output = `[${timeStr}] [${level.toUpperCase()}]`;
  if (prefix) {
    output += ` [${prefix}]`;
  }
  output += ` ${message}`;
  if (stackInfo) {
    output += ` (${stackInfo.file}:${stackInfo.line}:${stackInfo.column})`;
  }
  return output;
}
function createANSIOutput(level, message, prefix, stackInfo) {
  const timestamp = formatTimestamp();
  const timeStr = timestamp.slice(11, 23);
  let output = "";
  output += formatTimestampANSI(`[${timeStr}]`) + " ";
  output += formatLogLevelANSI(level) + " ";
  if (prefix) {
    output += formatPrefixANSI(prefix) + " ";
  }
  output += message;
  if (stackInfo) {
    output += " " + formatLocationANSI(`(${stackInfo.file}:${stackInfo.line}:${stackInfo.column})`);
  }
  return sanitizeEmojis(output);
}
function createBuildOutput(level, message, prefix, stackInfo) {
  const timestamp = formatTimestamp();
  const timeStr = timestamp.slice(11, 23);
  let output = "";
  if (supportsANSIColors()) {
    output += formatTimestampANSI(`[${timeStr}]`) + " ";
    switch (level) {
      case "info":
        output += BUILD_FORMATTERS.info(message);
        break;
      case "warn":
        output += BUILD_FORMATTERS.warning(message);
        break;
      case "error":
      case "critical":
        output += BUILD_FORMATTERS.error(message);
        break;
      default:
        output += `${formatLogLevelANSI(level)} ${message}`;
    }
  } else {
    output = `[${timeStr}] [${level.toUpperCase()}]`;
    if (prefix) {
      output += ` [${prefix}]`;
    }
    output += ` ${message}`;
  }
  if (stackInfo) {
    output += ` (${stackInfo.file}:${stackInfo.line}:${stackInfo.column})`;
  }
  return sanitizeEmojis(output);
}
function createCIOutput(level, message, prefix, stackInfo) {
  const timestamp = formatTimestamp();
  const timeStr = timestamp.slice(11, 23);
  let output = `[${timeStr}] [${level.toUpperCase()}]`;
  if (prefix) {
    output += ` [${prefix}]`;
  }
  output += ` ${message}`;
  if (stackInfo) {
    output += ` (${stackInfo.file}:${stackInfo.line}:${stackInfo.column})`;
  }
  return sanitizeEmojis(output);
}
function detectOptimalFormat() {
  const isCI = process.env.CI || process.env.GITHUB_ACTIONS || process.env.JENKINS_URL || process.env.GITLAB_CI || process.env.TRAVIS || process.env.CIRCLECI;
  process.env.NEXT_RUNTIME || process.argv.some((arg) => arg.includes("next"));
  if (isCI) {
    return "ci";
  }
  {
    return "build";
  }
}
function getConsoleMethod(level) {
  switch (level) {
    case "debug":
      return "log";
    case "info":
      return "info";
    case "warn":
      return "warn";
    case "error":
    case "critical":
      return "error";
    default:
      return "log";
  }
}
function createLogEntry(level, message, args, prefix, stackInfo) {
  return {
    timestamp: formatTimestamp(),
    level: level.toUpperCase(),
    prefix,
    message,
    args: args.slice(1),
    // Exclude the first message argument
    location: stackInfo ? {
      file: stackInfo.file,
      line: stackInfo.line,
      column: stackInfo.column,
      function: stackInfo.function
    } : void 0,
    raw: args
  };
}
function formatTablePlain(data, columns) {
  try {
    if (Array.isArray(data)) {
      if (data.length === 0) return "[Empty Array]";
      if (typeof data[0] !== "object") {
        return data.map((item, i) => `${i}: ${item}`).join("\n");
      }
      const keys = columns || Object.keys(data[0] || {});
      const header = keys.join("	");
      const rows = data.map(
        (item) => keys.map((key) => String(item[key] || "")).join("	")
      );
      return [header, ...rows].join("\n");
    }
    if (typeof data === "object" && data !== null) {
      return Object.entries(data).map(([key, value]) => `${key}: ${value}`).join("\n");
    }
    return String(data);
  } catch (error) {
    return `[Error formatting table: ${error}]`;
  }
}
function safeSerialize(obj, maxDepth = 3, currentDepth = 0) {
  if (currentDepth >= maxDepth) {
    return "[Max Depth Reached]";
  }
  if (obj === null) return null;
  if (obj === void 0) return "[undefined]";
  if (typeof obj === "function") return "[Function]";
  if (obj instanceof Error) return `[Error: ${obj.message}]`;
  if (obj instanceof Date) return obj.toISOString();
  if (typeof obj !== "object") return obj;
  if (Array.isArray(obj)) {
    return obj.map((item) => safeSerialize(item, maxDepth, currentDepth + 1));
  }
  const result = {};
  for (const [key, value] of Object.entries(obj)) {
    result[key] = safeSerialize(value, maxDepth, currentDepth + 1);
  }
  return result;
}
export {
  BUILD_FORMATTERS as B,
  createANSIOutput as a,
  createBuildOutput as b,
  createOutput as c,
  detectOptimalFormat as d,
  createCIOutput as e,
  ansiStyle as f,
  ansiColor as g,
  ansiBackground as h,
  ansiBold as i,
  ansiDim as j,
  ansiUnderline as k,
  formatLogLevelANSI as l,
  formatSuccessANSI as m,
  sanitizeEmojis as n,
  getConsoleMethod as o,
  createLogEntry as p,
  formatTablePlain as q,
  createPlainOutput as r,
  safeSerialize as s
};
//# sourceMappingURL=formatting-CYjT9yhO.js.map
